// @flow

import * as React from 'react';

import clsx from 'clsx';

import { useLocation, useNavigate, useOutletContext } from 'react-router-dom';

import type { Location } from 'react-router-dom';

import { View } from 'reshaped';

import type { StaticHandlerArgs } from '@gandi/app-runner/server';
import { selectCountItems } from '@gandi/cart';
import { NotificationsPlaceholder } from '@gandi/notifications';
import { Base, type BaseProps } from '@gandi/ui';

import { ModalBlock } from 'src/Reshaped/Modal/ModalBlock';
import Footer from 'src/components/Footer';
import {
  getIsCheckoutPage,
  getIsStudioPage,
} from 'src/components/helpers/pageMatchers';
import { useAppSelector } from 'src/hooks';
import { selectIsStudioUser } from 'src/modules/gandi-studio';
import type { FullState, PreFetchArgs, PreFetchReturn } from 'src/types';

import { CartSummary } from '../../components/CartSummary';
import {
  FunnelControls,
  type FunnelControlsProps,
} from '../../components/FunnelControls/FunnelControls';
import { FunnelWidgets } from '../../components/FunnelWidgets/FunnelWidgets';
import { Header } from '../../components/Header/Header';
import { SecondaryHeader } from '../../components/Header/SecondaryHeader';
import { LandingPagesControls } from '../../components/LandingPagesControls/LandingPagesControls';
import { LandingPagesWidgets } from '../../components/LandingPagesWidgets/LandingPagesWidgets';
import { Main } from '../../components/Main';
import PreferencesWidget from '../../components/PreferencesWidget/PreferencesWidget';
import type { DialogState } from '../../components/PreferencesWidget/types';
import { SkipLinks } from '../../components/SkipLinks';
import { StudioControls } from '../../components/StudioControls/StudioControls';
import { CartSummaryProvider } from '../../context/CartSummary';
import { ModalProvider } from '../../context/Modal';

import styles from './ShopLayout.scss';

export type Props = {
  backgroundColor?: 'surface',
  showCartSummary?: boolean,
  widgets?: React.Node,
  /** Optional secondary header */
  header?: React.Node,
  children?: React.Node,
  dashboardURL: string,
  preferencesDialog: DialogState,
  currentStep?: FunnelControlsProps['currentStep'],
};

const smallContainerPage = [
  'account-choice',
  'configure',
  'account',
  'mailboxes',
  'overview',
  'checkout',
  'payment-return',
  'awaiting-payment',
];

function isSmall(location: Location) {
  return smallContainerPage.some(page => location.pathname.includes(page));
}

function Background({
  backgroundColor,
  children,
}: {
  backgroundColor: BaseProps['backgroundColor'],
  children: React.Node,
}) {
  if (backgroundColor === 'surface' || backgroundColor === 'white') {
    return (
      <View backgroundColor="page" className={clsx(styles.root)}>
        {children}
      </View>
    );
  }

  return (
    <Base
      backgroundColor={backgroundColor || 'background'}
      className={clsx(styles.root)}
    >
      {children}
    </Base>
  );
}

function ShopLayout({
  showCartSummary,
  currentStep,
  header,
  ...props
}: Props): React.MixedElement {
  const navigate = useNavigate();

  const location = useLocation();
  const isStudioFlowPage = getIsStudioPage(location);
  const showFunnelWidgets = getIsCheckoutPage(location);
  const countItems = useAppSelector(selectCountItems);
  const isStudioUser = useAppSelector(selectIsStudioUser);

  const hasProducts = countItems > 0;

  const outletContext = useOutletContext();

  const isStudioFlow = isStudioFlowPage && isStudioUser;

  const controls = () => {
    switch (true) {
      case showFunnelWidgets && hasProducts:
        // steps
        return <FunnelControls currentStep={currentStep} />;
      case isStudioFlow:
        // Logo gandiShop || Logo + steps
        return <StudioControls currentStep={currentStep} />;
      default:
        // Logo + main menu
        return <LandingPagesControls />;
    }
  };

  React.useEffect(() => {
    // User deleted all cart products, redirect to empty cart screen.
    if (countItems === 0 && currentStep === 1) {
      navigate('/cart', { replace: true });
    }
  }, [countItems, navigate, currentStep]);

  const widgets = isStudioFlow ? undefined : showFunnelWidgets ? (
    <FunnelWidgets preferencesDialog={props.preferencesDialog} />
  ) : (
    <LandingPagesWidgets
      preferencesDialog={props.preferencesDialog}
      widgets={props.widgets}
    />
  );

  return (
    <Background
      backgroundColor={props.backgroundColor || outletContext?.backgroundColor}
    >
      <SkipLinks />

      <NotificationsPlaceholder className={styles.notifications} />

      <Header
        className={clsx(
          styles.header,
          showFunnelWidgets ? styles.funnelHeader : styles.landingPagesHeader,
        )}
        controls={controls()}
        widgets={widgets}
      />

      {!!header && <SecondaryHeader>{header}</SecondaryHeader>}

      <CartSummaryProvider>
        <ModalProvider>
          <>
            <Base
              className={
                showCartSummary ? styles.cart2ColumnWrapper : undefined
              }
            >
              <Base
                className={clsx(
                  styles.mainWrapper,
                  isSmall(location) && !showCartSummary
                    ? styles.mainWrapperSmall
                    : undefined,
                  showCartSummary ? styles.mainWrapperCart2Columns : undefined,
                )}
              >
                <Main className={styles.main}>{props.children}</Main>
                <ModalBlock />
              </Base>
              {showCartSummary && <CartSummary />}
            </Base>
            <Footer showCart2Column={showCartSummary} />
          </>
        </ModalProvider>
      </CartSummaryProvider>
    </Background>
  );
}

ShopLayout.preFetch = function preFetch(
  args: PreFetchArgs<{}, any, any, FullState>,
): PreFetchReturn<> {
  return PreferencesWidget.preFetch(args);
};

ShopLayout.handleRedirect = function handleRedirect(
  args: StaticHandlerArgs<>,
): Promise<void> | Promise<mixed> {
  return PreferencesWidget.handleRedirect(args);
};

export { ShopLayout };
